/* eslint-disable */
import { useEffect, useState } from 'react';
import OktaSignIn from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';

// Assets
import oktaLogo from '@assets/images/signin-logo.svg';

// Components
import Loader from '@atoms/Loader';

const oktaConfig = {
  issuer: import.meta.env.VITE_APP_OKTA_ISSUER as string,
  clientId: import.meta.env.VITE_APP_OKTA_CLIENT_ID as string,
  redirectUri: import.meta.env.VITE_APP_OKTA_REDIRECT_URI as string,
  baseURL: import.meta.env.VITE_APP_BASE_URL as string,
  scopes: ['openid', 'profile', 'email', 'groups'],
  pkce: true,
};

const ResetPassword = () => {
  const [loader, setLoader] = useState<boolean>(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      window.location.href = '/login';
      return;
    }

    const resetPasswordWidget = new OktaSignIn({
      flow: 'resetPassword',
      el: '#okta-reset-widget',
      recoveryToken: token,
      baseUrl: oktaConfig.baseURL,
      issuer: oktaConfig.issuer,
      clientId: oktaConfig.clientId,
      redirectUri: oktaConfig.redirectUri,
      useClassicEngine: true,
      logo: oktaLogo,
      features: {
        registration: false,
        showPasswordToggleOnSignInPage: true,
        hideSignOutLinkInMFA: true,
        webauthn: true,
      },
      colors: {
        brand: '#05417B',
      },
      i18n: {
        en: {
          'password.reset.title.generic': 'Reset your AUSPL Insurance Payment Portal password',
        },
      },
    });

    void resetPasswordWidget.showSignInAndRedirect();

    resetPasswordWidget.on('afterRender', (context: any) => {
      setLoader(false);

      // Added loader while clicking on Verify in MFA screen
      document.addEventListener('click', (e) => {
        const target = e.target as Element;
        if (target.matches('input[value="Verify"]')) {
          const code =
            (document.querySelector('input[name="answer"]') as HTMLInputElement)?.value ||
            (document.querySelector('input[name="passCode"]') as HTMLInputElement)?.value;

          if (code && code != '') {
            setLoader(true);
          }
        }
      });

      // To style the password requirements
      const explainEl = document.querySelector(
        '.okta-form-subtitle.o-form-explain[data-se="o-form-explain"]'
      );

      const passwordInputFields = document.querySelectorAll<HTMLInputElement>(
        '.password-reset input[type="password"], .password-reset input[type="text"]'
      );

      if (explainEl && explainEl.textContent?.includes('Password requirements:')) {
        // Original text
        const text = explainEl.textContent;

        // Remove "Password requirements:" from the string
        const cleaned = text.replace(/^Password requirements:\s*/i, '');

        // Split into individual rules
        const items = cleaned.split(',').map((s) => s.trim());

        // Create a container
        const container = document.createElement('div');

        // Add heading
        const title = document.createElement('p');
        title.textContent = 'Password requirements:';
        title.style.fontWeight = '600';
        title.style.paddingBottom = '10px';
        container.appendChild(title);

        // Create a bulleted list
        const ul = document.createElement('ul');
        ul.className = 'password-requirements-list';
        items.forEach((rule) => {
          if (rule) {
            const capitalized = rule.charAt(0).toUpperCase() + rule.slice(1);
            const li = document.createElement('li');
            li.textContent = capitalized;
            ul.appendChild(li);
          }
        });
        container.appendChild(ul);

        // Replace the old <p> with new container
        explainEl.replaceWith(container);
      }

      // To update toggle icon for password fields
      const updateToggleIcon = (input: HTMLInputElement, toggle: HTMLElement | null) => {
        if (input.type === 'password' && toggle) {
          toggle.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#555" viewBox="0 0 24 24">
              <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 
              11-7 11-7-4-7-11-7zm0
              12c-2.761 0-5-2.239-5-5s2.239-5
              5-5 5 2.239 5 5-2.239 5-5 5zm0-8c-1.654 
              0-3 1.346-3 3s1.346 3 3 3 3-1.346
              3-3-1.346-3-3-3z"/>
            </svg>
          `;
        } else if (toggle) {
          toggle.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#555" viewBox="0 0 24 24">
              <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 
              11-7 11-7-4-7-11-7zm0
              12c-2.761 0-5-2.239-5-5s2.239-5
              5-5 5 2.239 5 5-2.239 5-5 5zm9.707-16.293
              l-1.414-1.414-19 19 1.414 1.414 19-19z"/>
            </svg>
          `;
        }
      };

      passwordInputFields.forEach((input) => {
        if (!input.parentElement?.querySelector('.password-toggle')) {
          const toggle = document.createElement('span');
          toggle.className = 'password-toggle';
          toggle.style.cursor = 'pointer';
          toggle.style.position = 'absolute';
          toggle.style.right = '10px';
          toggle.style.top = '50%';
          toggle.style.transform = 'translateY(-50%)';

          // Initial icon sync
          updateToggleIcon(input, toggle);

          // Toggle on click
          toggle.onclick = () => {
            const currentValue = input.value;
            input.type = input.type === 'password' ? 'text' : 'password';
            input.value = currentValue;
            updateToggleIcon(input, toggle);
          };

          input.parentElement!.style.position = 'relative';
          input.parentElement!.appendChild(toggle);
        }
      });
      const resetBtn = document.querySelector<HTMLInputElement>(
        '.password-reset input[data-type="save"]'
      );
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          setTimeout(() => {
            let hasError = false;
            const errorContainer = document.querySelector('.o-form-error-container');
            if (errorContainer) {
              hasError = errorContainer?.children.length > 0 || false;
            }

            const allFilled = Array.from(passwordInputFields).every(
              (input) => input.value.trim() !== ''
            );

            if (!hasError && allFilled) {
              setLoader(true);
            }
          }, 30);

          document
            .querySelectorAll<HTMLInputElement>('.password-reset input[type="text"]')
            .forEach((input) => {
              input.type = 'password';
              if (input.parentElement && input.parentElement?.querySelector('.password-toggle')) {
                updateToggleIcon(input, input.parentElement.querySelector('.password-toggle'));
              }
            });
        });
      }

      document
        .querySelectorAll<HTMLInputElement>('.password-reset input[type="password"]')
        .forEach((input) => {
          input?.addEventListener('input', () => {
            if (input.type !== 'password') {
              input.type = 'password';
              if (input.parentElement && input.parentElement?.querySelector('.password-toggle')) {
                updateToggleIcon(input, input.parentElement.querySelector('.password-toggle'));
              }
            }
          });
        });

      passwordInputFields.forEach((input) => {
        // To remove browser default toggle
        const style = document.createElement('style');
        style.innerHTML = `
            .password-reset input[type="password"]::-ms-reveal,
            .password-reset input[type="password"]::-ms-clear,
            .password-reset input[type="text"]::-ms-reveal,
            .password-reset input[type="text"]::-ms-clear {
              display: none !important;
            };
            -webkit-text-security: none;
            text-security: none;   
          `;
        document.head.appendChild(style);

        // To restrict copy for password field
        input.addEventListener('copy', (e) => {
          e.preventDefault();
          const currentValue = input.value;
          input.value = currentValue;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
    });

    resetPasswordWidget.on('afterError', () => {
      setLoader(false);
    });

    return () => resetPasswordWidget.remove();
  }, []);

  return (
    <>
      <Loader display={loader} />
      <div id="okta-reset-widget" />
    </>
  );
};

export default ResetPassword;
